import axios, { AxiosError } from 'axios';
import { getVisitorId } from './session';
import taskManagerApi, { isProfileVerifyRequired } from '../api/taskManagerApi';
import { API_CONFIG } from '../config/api';

// 使用统一的API配置，移除硬编码

const CURRENT_TASK_IDS_KEY = 'structure_current_task_ids';

// 接口定义
export interface TaskCreateRequest {
  name: string;
  description?: string;
  service_name: string;
  task_type: string;
  handler_name: string;
  parameters?: any;
  priority?: number;
  callback_url?: string;
  timeout?: number;
  max_retries?: number;
  user_id?: string;
}

export interface TaskResponse {
  id: string;
  name: string;
  description?: string;
  service_name: string;
  task_type: string;
  handler_name: string;
  status: 'pending' | 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'timeout';
  progress: number;
  error?: string;
  result?: any;
  result_url?: string;
  parameters?: any;
  user_id?: string;
  user_email?: string;
  created_at: string;
  updated_at: string;
  started_at?: string;
  completed_at?: string;
  priority: number;
  max_retries: number;
  retry_count: number;
  timeout: number;
  worker_id?: string;
  // 新增：UI 元信息（后端可选注入）
  ui?: {
    display_in_list?: boolean;
    [key: string]: any;
  };
}

export interface HandlerResponse {
  id: string;
  service_name: string;
  task_type: string;
  handler_name: string;
  endpoint_url: string;
  description?: string;
  is_active: boolean;
  last_heartbeat?: string;
  created_at: string;
  updated_at: string;
}

class TaskManagerClient {
  private baseURL: string;

  constructor(baseURL: string = API_CONFIG.TASK_MANAGER_BASE) {
    this.baseURL = baseURL;
  }

  /**
   * 创建任务
   */
  async createTask(taskData: TaskCreateRequest): Promise<TaskResponse> {
    try {
      const headers = {
        'X-Visitor-ID': getVisitorId()
        // 不再需要手动传递 X-User-ID，后端会从认证cookie中自动获取
      };
      const response = await taskManagerApi.post('/tasks/', taskData, { headers });
      if (taskData.service_name === 'structure_prediction' || taskData.service_name === 'structure_design') {
        const taskIds = JSON.parse(sessionStorage.getItem(CURRENT_TASK_IDS_KEY) || '[]') as string[];
        sessionStorage.setItem(CURRENT_TASK_IDS_KEY, JSON.stringify([response.id, ...taskIds.filter(id => id !== response.id)].slice(0, 20)));
      }
      return response;
    } catch (error) {
      console.error('创建任务失败:', error);
      throw error;
    }
  }

  /**
   * 获取任务详情
   */
  async getTask(taskId: string): Promise<TaskResponse> {
    try {
      const response = await taskManagerApi.get(`/tasks/${taskId}`);
      return response;
    } catch (error) {
      console.error('获取任务详情失败:', error);
      throw error;
    }
  }

  async getCurrentTasks(
    serviceName: string,
    status?: string,
    taskType?: string,
    handlerName?: string
  ): Promise<TaskResponse[]> {
    const taskIds = JSON.parse(sessionStorage.getItem(CURRENT_TASK_IDS_KEY) || '[]') as string[];
    const tasks = await Promise.all(taskIds.map(async (taskId) => {
      try {
        return await this.getTask(taskId);
      } catch {
        return null;
      }
    }));

    return tasks.filter((task): task is TaskResponse =>
      !!task && task.service_name === serviceName &&
      (!status || task.status === status) &&
      (!taskType || task.task_type === taskType) &&
      (!handlerName || task.handler_name === handlerName)
    );
  }

  async getTasksWithCurrent(
    skip: number,
    limit: number,
    status: string | undefined,
    serviceName: string,
    taskType?: string,
    handlerName?: string
  ): Promise<TaskResponse[]> {
    let historyTasks: TaskResponse[] = [];
    try {
      historyTasks = await this.getTasks(skip, limit, status, serviceName, taskType, handlerName);
    } catch (error) {
      if (!isProfileVerifyRequired(error)) throw error;
    }

    const currentTasks = await this.getCurrentTasks(serviceName, status, taskType, handlerName);
    const tasksById = new Map(currentTasks.map(task => [task.id, task]));
    historyTasks.forEach(task => tasksById.set(task.id, task));
    return Array.from(tasksById.values());
  }

  /**
   * 获取任务列表
   */
  async getTasks(
    skip: number = 0,
    limit: number = 100,
    status?: string,
    service_name?: string,
    task_type?: string,
    handler_name?: string,
    user_id?: string,
    summary_only?: boolean
  ): Promise<TaskResponse[]> {
    try {
      const params = { skip, limit, status, service_name, task_type, handler_name, user_id, summary_only };
      const response = await taskManagerApi.get('/tasks/', { params });
      return response;
    } catch (error) {
      console.error('获取任务列表失败:', error);
      throw error;
    }
  }

  /**
   * 取消任务
   */
  async cancelTask(taskId: string): Promise<TaskResponse> {
    try {
      const response = await taskManagerApi.post(`/tasks/${taskId}/cancel`);
      return response;
    } catch (error) {
      console.error('取消任务失败:', error);
      throw error;
    }
  }

  /**
   * 删除任务（Admin控制台用）
   */
  async deleteTask(taskId: string): Promise<boolean> {
    try {
      const response = await taskManagerApi.delete(`/tasks/${taskId}`);
      return response.success;
    } catch (error: unknown) {
      console.error('删除任务失败:', error);
      // 检查是否是400错误（状态限制）
      if (error instanceof Error && 'response' in error && (error as AxiosError).response?.status === 400) {
        console.error('删除失败：只能删除已完成、失败、取消或超时的任务');
      }
      throw error;
    }
  }

  /**
   * 恢复任务（Admin控制台用）
   */
  async restoreTask(taskId: string): Promise<boolean> {
    try {
      const response = await taskManagerApi.post(`/tasks/${taskId}/restore`);
      return response.success;
    } catch (error) {
      console.error('恢复任务失败:', error);
      throw error;
    }
  }

  /**
   * 本地删除任务（仅前端页面使用，不调用后端API）
   */
  async deleteTaskLocal(taskId: string): Promise<boolean> {
    try {
      // 从localStorage中删除任务ID
      const deletedTasks = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
      if (!deletedTasks.includes(taskId)) {
        deletedTasks.push(taskId);
        localStorage.setItem('deletedTaskIds', JSON.stringify(deletedTasks));
      }
      return true;
    } catch (error) {
      console.error('本地删除任务失败:', error);
      throw error;
    }
  }

  /**
   * 批量本地删除任务（仅前端页面使用）
   */
  async batchDeleteTasksLocal(taskIds: string[]): Promise<boolean> {
    try {
      const deletedTasks = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
      // 修复 TypeScript 错误：使用 Array.from 替代展开运算符
      const combinedTasks = [...deletedTasks, ...taskIds];
      const uniqueTasks = Array.from(new Set(combinedTasks));
      localStorage.setItem('deletedTaskIds', JSON.stringify(uniqueTasks));
      return true;
    } catch (error) {
      console.error('批量本地删除任务失败:', error);
      throw error;
    }
  }

  /**
   * 获取已删除的任务ID列表
   */
  getDeletedTaskIds(): string[] {
    try {
      return JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
    } catch (error) {
      console.error('获取已删除任务列表失败:', error);
      return [];
    }
  }

  /**
   * 清空已删除的任务列表
   */
  clearDeletedTasks(): void {
    try {
      localStorage.removeItem('deletedTaskIds');
    } catch (error) {
      console.error('清空已删除任务列表失败:', error);
    }
  }

  /**
   * 批量删除任务（Admin控制台用）
   */
  async batchDeleteTasks(taskIds: string[]): Promise<any> {
    try {
      const response = await taskManagerApi.post('/tasks/batch-delete-tasks', taskIds);
      return response;
    } catch (error) {
      console.error('批量删除任务失败:', error);
      throw error;
    }
  }

  /**
   * 获取处理器列表
   */
  async getHandlers(
    skip: number = 0,
    limit: number = 100,
    service_name?: string,
    task_type?: string,
    is_active?: boolean
  ): Promise<HandlerResponse[]> {
    try {
      const params = { skip, limit, service_name, task_type, is_active };
      const response = await taskManagerApi.get('/handlers/', { params });
      return response;
    } catch (error) {
      console.error('获取处理器列表失败:', error);
      throw error;
    }
  }

  /**
   * 注册处理器
   */
  async registerHandler(handlerData: {
    service_name: string;
    task_type: string;
    handler_name: string;
    endpoint_url: string;
    description?: string;
  }): Promise<HandlerResponse> {
    try {
      const response = await taskManagerApi.post('/handlers/', handlerData);
      return response;
    } catch (error) {
      console.error('注册处理器失败:', error);
      throw error;
    }
  }

  /**
   * 发送心跳
   */
  async sendHeartbeat(handlerId: string): Promise<HandlerResponse> {
    try {
      const response = await taskManagerApi.post(`/handlers/${handlerId}/heartbeat`);
      return response;
    } catch (error) {
      console.error('发送心跳失败:', error);
      throw error;
    }
  }

  /**
   * 健康检查
   */
  async checkHealth(): Promise<boolean> {
    try {
      const response = await axios.get(`${this.baseURL}/health`);
      return response.status === 200;
    } catch (error) {
      console.error('健康检查失败:', error);
      return false;
    }
  }

  /**
   * 获取可用服务列表
   */
  async getAvailableServices(): Promise<HandlerResponse[]> {
    try {
      const response = await taskManagerApi.get('/handlers/', { params: { is_active: true } });
      return response;
    } catch (error) {
      console.error('获取可用服务失败:', error);
      throw error;
    }
  }

  /**
   * 查找特定处理器
   */
  async findHandler(serviceName: string, taskType: string, handlerName: string): Promise<HandlerResponse | null> {
    try {
      const response = await taskManagerApi.post('/handlers/find', {
        service_name: serviceName,
        task_type: taskType,
        handler_name: handlerName
      });
      return response;
    } catch (error) {
      console.error('查找处理器失败:', error);
      return null;
    }
  }

  /**
   * 获取当前用户自己的任务（后端已做用户隔离）
   * 仅展示长时任务（前端过滤）
   */
  async listMyLongRunningTasks(params: {
    skip?: number;
    limit?: number;
    service_name?: string;
    task_type?: string;
    handler_name?: string;
    status?: string;
  } = {}): Promise<any[]> {
    try {
      const response = await taskManagerApi.get('/tasks', { params });
      // 仅展示 ui.display_in_list === true 或 长时白名单
      return (response || []).filter((t: TaskResponse) => shouldDisplayInList(t));
    } catch (error) {
      console.error('获取任务列表失败:', error);
      throw error;
    }
  }
}

// 创建客户端实例
export const taskManagerClient = new TaskManagerClient();

// 导出便捷函数
export const createTask = (taskData: TaskCreateRequest) => 
  taskManagerClient.createTask(taskData);

export const getTask = (taskId: string) => 
  taskManagerClient.getTask(taskId);

export const getTasks = (skip?: number, limit?: number, status?: string, service_name?: string, task_type?: string, handler_name?: string, user_id?: string, summary_only?: boolean) =>
  taskManagerClient.getTasks(skip, limit, status, service_name, task_type, handler_name, user_id, summary_only);

export const getTasksWithCurrent = (skip: number, limit: number, status: string | undefined, serviceName: string, taskType?: string, handlerName?: string) =>
  taskManagerClient.getTasksWithCurrent(skip, limit, status, serviceName, taskType, handlerName);

export const getTasksByUser = (user_id: string, skip?: number, limit?: number, status?: string, service_name?: string, task_type?: string, handler_name?: string) =>
  taskManagerClient.getTasks(skip, limit, status, service_name, task_type, handler_name, user_id);

export const cancelTask = (taskId: string) => 
  taskManagerClient.cancelTask(taskId);

// Admin控制台使用的数据库删除
export const deleteTask = (taskId: string) => 
  taskManagerClient.deleteTask(taskId);

export const restoreTask = (taskId: string) =>
  taskManagerClient.restoreTask(taskId);

// 前端页面使用的本地删除
export const deleteTaskLocal = (taskId: string) => 
  taskManagerClient.deleteTaskLocal(taskId);

export const batchDeleteTasks = (taskIds: string[]) => 
  taskManagerClient.batchDeleteTasks(taskIds);

// 前端页面使用的批量本地删除
export const batchDeleteTasksLocal = (taskIds: string[]) => 
  taskManagerClient.batchDeleteTasksLocal(taskIds);

// 获取已删除任务列表的便捷函数
export const getDeletedTaskIds = () => 
  taskManagerClient.getDeletedTaskIds();

// 清空已删除任务列表的便捷函数
export const clearDeletedTasks = () => 
  taskManagerClient.clearDeletedTasks();

export const getHandlers = (skip?: number, limit?: number, service_name?: string, task_type?: string, is_active?: boolean) =>
  taskManagerClient.getHandlers(skip, limit, service_name, task_type, is_active);

export const registerHandler = (handlerData: any) => 
  taskManagerClient.registerHandler(handlerData);

export const checkHealth = () => 
  taskManagerClient.checkHealth();

export const getAvailableServices = () => 
  taskManagerClient.getAvailableServices();

// 新增：前端统一判断是否为长时任务并在列表展示
export function shouldDisplayInList(task: TaskResponse): boolean {
  // 1) 如果后端明确设置了 ui.display_in_list = true，则显示
  if (task?.ui && task.ui.display_in_list === true) {
    return true;
  }
  
  // 2) 如果后端明确设置了 ui.display_in_list = false，但任务类型在白名单中，仍然显示
  // 这是为了兼容后端可能错误设置的情况
  const service = (task.service_name || '').toLowerCase();
  const type = (task.task_type || '').toLowerCase();
  const handler = (task.handler_name || '').toLowerCase();

  // 长时任务白名单：esmfold / zdock / megadock / equidock / prodigy / 抗原表位预测 / 抗体结构预测 / proteinmpnn / protenix
  const candidates = [service, type, handler].join(' ');
  const keywords = [
    'esmfold',
    'zdock',
    'megadock',
    'equidock',
    'prodigy',
    'docking',
    'epitope',
    'bepipred',
    'antigen',
    'antibody_structure',
    'antibody structure',
    'proteinmpnn',
    'protein_mpnn',
    'structure_design',
    'protenix',
    'protenix_predict'
  ];
  
  // 3) 如果在白名单中，则显示
  if (keywords.some(k => candidates.includes(k))) {
    return true;
  }
  
  // 4) 其他情况不显示
  return false;
}
